<template>
  <div class="rule-form-step rule-form-step--start rule-form-step--start--step-3 flex max-w-4xl flex-col gap-5 py-5">
    <adset-name-block :entity="entity" :disabled="$parent.readonlyMode" />

    <conversion-odax-block
      v-if="isFbOdax"
      :entity="entity"
      :disabled="$parent.readonlyMode"
    />
    <conversion-block
      v-else
      :entity="entity"
      :disabled="$parent.readonlyMode"
    />

    <target-audience-block
      ref="targetAudienceBlock"
      :entity="entity"
      :disabled="$parent.readonlyMode"
      :advantage-plus-available="audienceCompatibilityValidation.advantage_plus.available"
      :advantage-plus-message="audienceCompatibilityValidation.advantage_plus.message"
      :advantage-plus-message-type="audienceCompatibilityValidation.advantage_plus.message_type"
      :audience-picker-message="audienceCompatibilityValidation.audience_picker.message"
      :audience-picker-message-type="audienceCompatibilityValidation.audience_picker.message_type"
      :disabled-audiences="audienceCompatibilityValidation.disabled_audiences"
    />

    <demographic-target-block
      :entity="entity"
      :disabled="$parent.readonlyMode"
      :target-audience-block-is-loading="isMounted ? $refs.targetAudienceBlock.isLoading : true"
      :age-available="audienceCompatibilityValidation.age.available"
      :age-message="audienceCompatibilityValidation.age.message"
      :age-message-type="audienceCompatibilityValidation.age.message_type"
      :gender-available="audienceCompatibilityValidation.gender.available"
      :gender-message="audienceCompatibilityValidation.gender.message"
      :gender-message-type="audienceCompatibilityValidation.gender.message_type"
    />

    <geotarget-block
      :entity="entity"
      :disabled="$parent.readonlyMode"
      :target-audience-block-is-loading="isMounted ? $refs.targetAudienceBlock.isLoading : true"
      :geo-available="audienceCompatibilityValidation.geo.available"
      :geo-message="audienceCompatibilityValidation.geo.message"
      :geo-message-type="audienceCompatibilityValidation.geo.message_type"
      :language-available="audienceCompatibilityValidation.language.available"
      :language-message="audienceCompatibilityValidation.language.message"
      :language-message-type="audienceCompatibilityValidation.language.message_type"
    />

    <adset-budget-block
      v-if="isFbOdax"
      :entity="entity"
      :disabled="$parent.readonlyMode"
    />
    <adset-budget-non-odax-block
      v-else
      :entity="entity"
      :disabled="$parent.readonlyMode"
    />

    <attribution-window-odax-block
      v-if="isFbOdax"
      :entity="entity"
      :disabled="$parent.readonlyMode"
    />
    <attribution-window-block
      v-else
      :entity="entity"
      :disabled="$parent.readonlyMode"
    />

    <dsa-block :entity="entity" :disabled="$parent.readonlyMode" />

    <ad-placement-block :entity="entity" :disabled="$parent.readonlyMode" />
  </div>
</template>

<script>
import AudienceCompatibilityValidation from '@/descriptors/AudienceCompatibilityValidation.js';
import RuleFormStepMixin from '@/mixins/RuleForm/Step.js';
import AudienceService from '@/services/Audience';
import AdPlacementBlock from '@/ui/components/RuleForm/Blocks/AdPlacementBlock.vue';
import AdsetBudgetBlock from '@/ui/components/RuleForm/Blocks/AdsetBudgetBlock.vue';
import AdsetBudgetNonOdaxBlock from '@/ui/components/RuleForm/Blocks/AdsetBudgetNonOdaxBlock.vue';
import AdsetNameBlock from '@/ui/components/RuleForm/Blocks/AdsetNameBlock.vue';
import AttributionWindowBlock from '@/ui/components/RuleForm/Blocks/AttributionWindowBlock.vue';
import AttributionWindowOdaxBlock from '@/ui/components/RuleForm/Blocks/AttributionWindowOdaxBlock.vue';
import ConversionBlock from '@/ui/components/RuleForm/Blocks/ConversionBlock.vue';
import ConversionOdaxBlock from '@/ui/components/RuleForm/Blocks/ConversionOdaxBlock.vue';
import DemographicTargetBlock from '@/ui/components/RuleForm/Blocks/DemographicTargetBlock.vue';
import DsaBlock from '@/ui/components/RuleForm/Blocks/DsaBlock.vue';
import GeotargetBlock from '@/ui/components/RuleForm/Blocks/GeotargetBlock.vue';
import TargetAudienceBlock from '@/ui/components/RuleForm/Blocks/TargetAudienceBlock.vue';
import debounce from '@/utils/debounce.js';

export default {
  name: 'StartRuleFormStep3',
  components: {
    AdPlacementBlock,
    AdsetBudgetBlock,
    AdsetBudgetNonOdaxBlock,
    AdsetNameBlock,
    AttributionWindowBlock,
    AttributionWindowOdaxBlock,
    ConversionBlock,
    ConversionOdaxBlock,
    DemographicTargetBlock,
    DsaBlock,
    GeotargetBlock,
    TargetAudienceBlock,
  },
  mixins: [ RuleFormStepMixin ],
  data() {
    return {
      isMounted: false,
      isValidatingAudience: false,
      audienceCompatibilityValidation: new AudienceCompatibilityValidation(),
      debouncedValidateAudienceCompatibility: debounce(this.validateAudienceCompatibility, 25),
    };
  },
  watch: {
    'entity.settings.adset_targeting.age_min': {
      handler: 'debouncedValidateAudienceCompatibility',
    },
    'entity.settings.adset_targeting_advantage_plus_audience': {
      handler: 'debouncedValidateAudienceCompatibility',
    },
    'entity.settings.adset_targeting_audiences': {
      handler: 'debouncedValidateAudienceCompatibility',
    },
    'entity.settings.adset_included_custom_audiences': {
      handler: 'debouncedValidateAudienceCompatibility',
    },
    'entity.settings.adset_targeting.geo_locations': {
      handler: 'debouncedValidateAudienceCompatibility',
      deep: true,
    },
  },
  mounted() {
    this.isMounted = true;
    this.debouncedValidateAudienceCompatibility();
  },
  methods:{
    validateAudienceCompatibility() {
      this.isValidatingAudience = true;

      const adAccountId = this.entity.ad_account.id;
      const adsetTargeting = this.entity.settings.adset_targeting || {};
      const minAge = adsetTargeting.age_min;
      const isAdvantagePlus = this.entity.settings.adset_targeting_advantage_plus_audience;
      const audiences = [
        ...(this.entity.settings.adset_targeting_audiences || []),
        ...(this.entity.settings.adset_included_custom_audiences || []),
      ];
      const geoLocations = adsetTargeting.geo_locations?.toObject() || [];
      const specialAdCategories = this.entity.output_campaign.special_ad_categories || [];
      const specialAdCategoryCountries = this.entity.output_campaign.special_ad_category_country || [];

      AudienceService.validateAudienceCompatibility(
        adAccountId,
        minAge,
        isAdvantagePlus,
        audiences,
        geoLocations,
        specialAdCategories,
        specialAdCategoryCountries,
      ).then(validation => {
        this.audienceCompatibilityValidation = validation;
        return validation;
      }).catch(err => {
        this.audienceCompatibilityValidation = new AudienceCompatibilityValidation();
        return err;
      }).finally(() => {
        this.isValidatingAudience = false;
      });
    },
    validate() {
      if (!this.validateBlocks()) {
        return false;
      }

      if (this.isFbOdax) {
        for (const promotedObjectField of this.fbOdaxPromotedObjects) {
          if (!this.entity.settings[promotedObjectField]) {
            this.error(this.tr(':field is required!', { ':field': this.tr('field.settings.' + promotedObjectField) }));
            return false;
          }
        }
      }

      if (this.isValidatingAudience) {
        this.infoToast(this.tr('Please wait while we validate the Advantage+ audience.'));
        return false;
      }

      if (this.audienceCompatibilityValidation.hasError) {
        this.error(this.audienceCompatibilityValidation.firstErrorMessage);
        return false;
      }

      return true;
    },
  },
};
</script>
